import React from 'react';
import { Activity, BarChart3, Target, TrendingUp, Users } from 'lucide-react';

const RoiTab = ({ ctx }) => {
  const { usage, roiData, saving } = ctx;

  return (
<div data-testid="roi-tab">
  <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>ROI Calculator</h1>
  <p className="text-xs mb-5" style={{ color: '#999' }}>
    Dynamic return-on-investment analysis based on your team&apos;s usage. Present this to leadership to justify continued investment.
  </p>

  {roiData?.demo_data && (
    <div className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium mb-4" style={{ backgroundColor: '#EFF6FF', color: '#1E40AF', border: '1px solid #BFDBFE' }}>
      <BarChart3 size={14} />
      Projections based on sample team data — will auto-update with real usage metrics
    </div>
  )}

  {roiData && (
    <>
      {/* Hero ROI card */}
      <div className="rounded-2xl p-6 mb-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1A1209, #3D2E1A)' }} data-testid="roi-hero">
        <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10" style={{ background: '#10B981', filter: 'blur(80px)', transform: 'translate(20%, -30%)' }} />
        <div className="relative z-10">
          <p className="text-[10px] font-semibold uppercase tracking-widest mb-2" style={{ color: '#F5831F' }}>Total Annual Return</p>
          <div className="flex items-baseline gap-3 mb-1">
            <span className="text-5xl font-black" style={{ color: '#10B981' }}>${roiData.total_annual_savings?.toLocaleString()}</span>
            <span className="text-lg font-bold" style={{ color: '#F5831F' }}>{roiData.roi_multiple}x ROI</span>
          </div>
          <p className="text-xs" style={{ color: '#A89880' }}>
            Platform cost: ${roiData.annual_platform_cost?.toLocaleString()}/yr | Net: ${roiData.net_roi?.toLocaleString()}/yr | Payback: {roiData.payback_period_months} months
          </p>
        </div>
      </div>

      {/* Input assumptions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Team Size', value: roiData.inputs?.team_size, icon: Users, color: '#3B82F6' },
          { label: 'Total Sessions', value: roiData.inputs?.total_sessions?.toLocaleString(), icon: Activity, color: '#F5831F' },
          { label: 'Avg Salary', value: `$${(roiData.inputs?.avg_salary || 0).toLocaleString()}`, icon: TrendingUp, color: '#059669' },
          { label: 'Cost/Member/Mo', value: `$${roiData.inputs?.cost_per_member_month}`, icon: Target, color: '#8B5CF6' },
        ].map(m => (
          <div key={m.label} className="bg-white rounded-xl p-4 text-center" style={{ border: '1px solid #E8E8E8' }}>
            <m.icon size={16} style={{ color: m.color }} className="mx-auto mb-1" />
            <p className="text-lg font-black" style={{ color: '#1A1209' }}>{m.value}</p>
            <p className="text-[10px] font-medium" style={{ color: '#999' }}>{m.label}</p>
          </div>
        ))}
      </div>

      {/* Savings breakdown */}
      <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E8E8E8' }} data-testid="savings-breakdown">
        <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: '#1A1209' }}>
          <TrendingUp size={14} style={{ color: '#10B981' }} /> Savings Breakdown
        </h3>
        <div className="space-y-4">
          {roiData.savings_breakdown?.map((s, i) => {
            const colors = ['#10B981', '#3B82F6', '#F59E0B', '#8B5CF6'];
            const pct = Math.round((s.amount / Math.max(roiData.total_annual_savings, 1)) * 100);
            return (
              <div key={i} data-testid={`saving-${i}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <h4 className="text-xs font-bold" style={{ color: '#1A1209' }}>{s.category}</h4>
                  <span className="text-sm font-black" style={{ color: colors[i] }}>${s.amount?.toLocaleString()}</span>
                </div>
                <div className="w-full h-3 rounded-full mb-1" style={{ backgroundColor: '#F0F0F0' }}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: colors[i] }} />
                </div>
                <p className="text-[10px]" style={{ color: '#999' }}>{s.description}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-5 pt-4 flex items-center justify-between" style={{ borderTop: '2px solid #F0F0F0' }}>
          <span className="text-xs font-bold uppercase" style={{ color: '#999' }}>Total Annual Savings</span>
          <span className="text-xl font-black" style={{ color: '#10B981' }}>${roiData.total_annual_savings?.toLocaleString()}</span>
        </div>
      </div>
    </>
  )}
</div>
  );
};

export default RoiTab;
